import { createFileRoute } from "@tanstack/react-router";
import { formatDollars } from "common/utils/formatDollars";
import Page from "lib/components/layout/Page";
import type { ShowroomState } from "lib/components/showroom2/state/showroomState";
import { useAppRouterParams, procurePath } from "lib/routeHelpers";
import { useMemo, useState } from "react";
import cx from "ui/cx";
import useBroadcastChannelSync from "ui/hooks/useBroadcastChannelSync";
import SplitFlex from "ui/layouts/SplitFlex";
import styles from "./index.module.scss";

const OPTION_STATES = 2176782336;

export const SHEETS_DEFAULT_PAGE = "FIRST FLOOR PLAN";

function IBSSplitPage() {
  const showOverlay = useMemo(() => {
    if (typeof window === "undefined") {
      return false;
    }
    const params = new URLSearchParams(window.location.search);
    return params.get("fuzzer") !== "0";
  }, []);
  const { builderId, id: studioDocId, locationPlanId } = useAppRouterParams();
  const [startOverlay, setStartOverlay] = useState(showOverlay);

  useBroadcastChannelSync({
    setState: setStartOverlay,
    channelId: showOverlay ? studioDocId : undefined,
    channelName: "demo/ibs-2024/showroom_startModal",
  });

  return (
    <Page pageTitle="IBS 2024 Demo" className={styles.bigscreen}>
      <div className={styles.banner}>
        <div>Instant Estimates</div>
        <div>Automatic CDs</div>
      </div>
      <SplitFlex className={styles.split} canResize={false}>
        <iframe
          src={procurePath({
            studioDocId,
            communityPlanId: locationPlanId,
            builderId,
          })}
          width="100%"
          height="100%"
        />
        <iframe
          src={`/demo/sheets?builderId=${builderId}&planId=${locationPlanId}&page=${encodeURIComponent(
            SHEETS_DEFAULT_PAGE,
          )}`}
          width="100%"
          height="100%"
        />
      </SplitFlex>
      {startOverlay && <StartOverlay studioDocId={studioDocId ?? ""} />}
    </Page>
  );
}

function StartOverlay({ studioDocId }: { studioDocId: string }) {
  const [pricing, setPricing] = useState<ShowroomState["pricing"]>();

  useBroadcastChannelSync({
    setState: setPricing,
    channelId: studioDocId,
    channelName: "demo/ibs-2024/showroom_summary",
  });
  if (!pricing || !pricing.optionsCount) {
    return null;
  }
  return (
    <div className={styles.startOverlay}>
      <div className={styles.options}>
        <div className={styles.description}>
          Home # <span>{Math.floor(Math.random() * (OPTION_STATES - 2) + 1).toLocaleString()}</span>{" "}
          <span className={styles.gray}>*</span>
        </div>
        <div className={styles.stats}>
          <div className={styles.description}>
            Sales Price: <span>{formatDollars(pricing.totalPrice)}</span>
          </div>
          <div className={styles.description}>
            Configured Option{pricing.optionsCount !== 1 ? "s" : ""}: <span>{pricing.optionsCount}</span>
          </div>
        </div>
      </div>

      <div className={cx(styles.smallDescription, styles.gray)}>
        *From <span>billions</span> of possible home configurations.
      </div>
    </div>
  );
}

export const Route = createFileRoute("/demo/connected/bigscreen")({
  component: IBSSplitPage,
});
